"use client";

import { useState, useEffect } from "react";
import { isValidEmail, cn } from "@/lib/utils";
import { useMainLayout } from "@/app/context/mainLayoutProvider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import GondolaLogo from "@/components/ui/svgComponents/GondolaLogo";
import useLandingPageInvite from "@/hooks/client/useLandingPageInvite";
import useLiveFeedRotation from "@/hooks/client/useLiveFeedRotation";
import { GoogleAuthModal } from "@/components/modal/googleAuthModal";
import { LIVE_FEED_ITEMS } from "./liveFeedItems";

const Hero: React.FC = () => {
  const [emailAddress, setEmailAddress] = useState("");
  const [googleAuthModalOpen, setGoogleAuthModalOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const { index: liveFeedIndex, visible: liveFeedVisible } =
    useLiveFeedRotation(LIVE_FEED_ITEMS.length);

  const { isWebView, isWebViewLoaded } = useMainLayout();

  const { joinWaitlist, loading } = useLandingPageInvite();

  const isValid = isValidEmail(emailAddress);

  // Preload auth modal images early for faster loading
  useEffect(() => {
    const googleImg = new Image();
    const microsoftImg = new Image();
    googleImg.src = "/assets/logos/google.svg";
    microsoftImg.src = "/assets/logos/microsoft.svg";
  }, []);

  // Defer video rendering until after first paint
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const startVideo = () => {
      requestAnimationFrame(() => {
        setTimeout(() => setShowVideo(true), 300);
      });
    };

    if (document.readyState === "complete") {
      startVideo();
    } else {
      window.addEventListener("load", startVideo);
      return () => window.removeEventListener("load", startVideo);
    }
  }, []);

  return (
    <div
      data-testid="landing-hero"
      className="-mb-16 h-[calc(100vh-160px)] min-h-[770px] lg:-mb-0 lg:h-[calc(100vh)] lg:min-h-0"
    >
      <div className="relative -top-[100px] h-full w-full">
        {/* Video Background Layer */}
        <div className="absolute inset-0">
          {/* Poster image - loads immediately for LCP */}
          <picture className="absolute inset-0 z-0">
            <source
              srcSet="https://assets.gondola.ai/hero-poster-desktop.avif"
              media="(min-width: 1024px)"
              type="image/avif"
            />
            <source
              srcSet="https://assets.gondola.ai/hero-poster-mobile.avif"
              type="image/avif"
            />
            <img
              src="https://assets.gondola.ai/hero-poster-mobile.webp"
              alt=""
              className="h-full w-full object-cover"
              width={1280}
              height={720}
              sizes="100vw"
              fetchPriority="high"
              aria-hidden="true"
            />
          </picture>
          {/* Video elements - rendered after first paint */}
          {showVideo && (
            <>
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="none"
                className="absolute inset-0 z-10 hidden h-full w-full object-cover lg:block"
              >
                <source
                  src="https://assets.gondola.ai/HeroVideo_MK3.mp4"
                  type="video/mp4"
                />
              </video>
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="none"
                className="absolute inset-0 z-10 block h-full w-full object-cover lg:hidden"
              >
                <source
                  src="https://assets.gondola.ai/HeroVideo_MK3_Mobile.mp4"
                  type="video/mp4"
                />
              </video>
            </>
          )}
        </div>

        {/* Gradient Overlay Layer */}
        <div
          className="absolute inset-0 z-20"
          style={{
            background:
              "linear-gradient(0deg, rgba(2, 12, 27, 0.60) 0%, rgba(2, 12, 27, 0.60) 100%)",
          }}
        />

        {/* Main Content Layer. `flex-col` + the widget below as a second
            child (instead of the widget being a separate `absolute`
            sibling) makes overlap with it structurally impossible on
            mobile: two normal-flow items in a column can't occupy the same
            space, so there's no pixel-budget to tune against a fold-margin
            constraint pulling the opposite way — the failure mode that
            made pure `min-height` tuning against both constraints
            simultaneously impossible once the widget's real rendered
            height was accounted for (3 lines of body text at the time;
            no longer pinned by a `line-clamp`, so treat it as variable). At `lg`+ the widget pulls itself out
            of this flow via its own `lg:absolute` (see below), leaving the
            content column as the only flex item — centered exactly as
            before, unaffected by `flex-col` since a single item centers
            the same regardless of axis. */}
        <div className="relative z-30 flex h-full max-h-[100svh] w-full flex-col items-center justify-start gap-6 pb-6 pt-20 lg:max-h-none lg:justify-center lg:pb-0 lg:pt-0">
          <div
            data-testid="hero-content"
            className="relative mt-auto w-full max-w-[960px] px-4 lg:mt-0"
          >
            <div className="inline-flex w-full max-w-[960px] flex-col items-center justify-start gap-5 sm:gap-9">
              {/* As seen in section */}
              <div className="flex items-center gap-3">
                <span className="text-center font-figtree text-base font-normal leading-6 text-foreground">
                  As seen in
                </span>
                <div className="flex items-center gap-2">
                  <a
                    href="https://www.nytimes.com/2025/08/19/travel/ai-travel-tools-test.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-6 w-6 rounded-full shadow-[4px_2px_4px_0px_rgba(2,12,27,0.10)]"
                  >
                    <div className="h-6 w-6 overflow-hidden rounded-full bg-pure-white p-1">
                      <img
                        className="h-full w-full object-contain"
                        src="/assets/landing-page/read-about-us/nyt1.png"
                        alt="New York Times"
                        width={36}
                        height={45}
                        loading="lazy"
                      />
                    </div>
                  </a>
                  <a
                    href="https://www.cbsnews.com/news/ai-travel-planning-tools/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-6 w-6 rounded-full shadow-[4px_2px_4px_0px_rgba(2,12,27,0.10)]"
                  >
                    <div className="h-6 w-6 overflow-hidden rounded-full bg-pure-white p-1">
                      <img
                        className="h-full w-full object-contain"
                        src="/assets/landing-page/read-about-us/image28.png"
                        alt="CBS News"
                        width={72}
                        height={48}
                        loading="lazy"
                      />
                    </div>
                  </a>
                  <a
                    href="https://www.nerdwallet.com/travel/learn/gondola-review"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-6 w-6 rounded-full shadow-[4px_2px_4px_0px_rgba(2,12,27,0.10)]"
                  >
                    <div className="h-6 w-6 overflow-hidden rounded-full bg-pure-white p-1">
                      <img
                        className="h-full w-full object-contain"
                        src="/assets/landing-page/read-about-us/NerdWallet_idClaXtDUJ_0 1.png"
                        alt="NerdWallet"
                        width={42}
                        height={42}
                        loading="lazy"
                      />
                    </div>
                  </a>
                </div>
              </div>

              {/* Main Headline */}
              <h1 className="w-full text-center font-figtree text-5xl font-semibold leading-none tracking-[-1px] text-foreground lg:text-8xl lg:leading-none">
                Earn More Points
                <br />
                Save More Money
              </h1>

              {/* Sub-headline */}
              <p className="w-full max-w-[498px] text-center font-figtree text-lg font-medium leading-relaxed text-foreground lg:text-xl lg:leading-relaxed">
                AI that finds your best rate, triple stacks points,{" "}
                <br className="hidden lg:inline" />
                and saves you money after you book.
              </p>

              {/* CTA Section */}
              <div
                className={
                  isWebViewLoaded && isWebView
                    ? "flex w-full items-center justify-center"
                    : "inline-flex items-center justify-start gap-3"
                }
              >
                {isWebViewLoaded && isWebView ? (
                  <div className="flex w-full max-w-xs flex-col items-center gap-4">
                    <Input
                      className="inline-flex h-11 w-full items-center justify-center gap-2.5 rounded-lg bg-background px-6 py-2.5 font-figtree text-base font-normal leading-6 text-foreground outline outline-1 outline-offset-[-1px] placeholder:text-muted-foreground"
                      placeholder="Enter your email"
                      value={emailAddress}
                      onChange={(e) =>
                        setEmailAddress(e.target.value.toLowerCase())
                      }
                      disabled={loading}
                      type="email"
                    />
                    <Button
                      onClick={() => joinWaitlist(emailAddress)}
                      disabled={loading || !isValid}
                      loading={loading}
                      variant="orange-gradient"
                      className="w-full font-figtree text-lg leading-7 text-pure-white"
                    >
                      Join Gondola
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => setGoogleAuthModalOpen(true)}
                    variant="orange-gradient"
                    className="font-figtree text-lg leading-7 text-pure-white"
                  >
                    Join Gondola
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Live activity widget — decorative, rotates through mock entries,
              styled after an iOS notification, fades in/out. Sizing is
              derived from the Figma reference (node 6802:4009) but not
              copied from it: Figma expresses every dimension relative to
              its own 29px text, and scaling that down literally would land
              the body text near 10px. Instead both breakpoints keep the
              reference's *rhythm* (icon ≈ 2.4x the body font, gap ≈ 1x)
              at a readable text size — `text-base` with a 38px icon on
              desktop, `text-sm` with a 36px icon below `lg`. Vertical
              padding is deliberately tighter than horizontal on desktop
              (`lg:py-3.5` vs `lg:px-4`) to keep the card short; that is a
              requested deviation from the reference's uniform padding, not
              an oversight.

              The body never truncates. It uses `text-pretty`, NOT
              `text-balance`: `balance` equalizes line lengths by shortening
              every line, which left each one filling only ~74-84% of the
              column and a visible ragged gutter down the right-hand side —
              the opposite of the Figma, whose body fills its column. With
              `pretty` the lines fill ~98% and only the final line is short,
              which is ordinary typography. Do not "improve" this back to
              `text-balance`. It has no `line-clamp`, so its height is
              bounded only by the copy in `liveFeedItems.ts` (which is why
              that file's test pins a max body length — the body's height
              feeds directly into this hero's fold margin). Browsers without
              `text-wrap: balance` fall back to normal wrapping.

              The headline is the opposite trade. Below `lg` it has neither
              `whitespace-nowrap` nor `truncate`, so on a narrow phone it
              wraps rather than being ellipsis-cut. At `lg`+ it regains both
              (`lg:whitespace-nowrap` on the row, `lg:truncate` on the `p`):
              at desktop width the headline has ~300px of slack, so truncation
              never fires in practice, but without it a future longer headline
              would silently overflow the card edge — `white-space` inherits
              and no ancestor clips.

              Below `lg`, this is a normal flow sibling of the content
              column above (not `absolute`) — see the parent's comment for
              why: an `absolute bottom-X` widget independent of the centered
              content above it made "no overlap" and "stays above the fold"
              two constraints pulling against the same pixel budget in
              opposite directions, and no value satisfied both at once. At
              `lg`+ it reclaims its original `absolute` bottom-pinned
              bottom-pinned position. */}
          <div
            data-testid="hero-notification"
            className="relative z-30 mt-auto w-full max-w-[560px] px-4 lg:absolute lg:bottom-6 lg:left-1/2 lg:mt-0 lg:max-w-[532px] lg:-translate-x-1/2"
          >
            <div
              className={cn(
                // Plain `flex` (fills the centered wrapper above) instead of
                // `inline-flex`-hug-content: every entry gets the exact same
                // pill width, so the box never resizes or looks off-center
                // as it rotates through entries of different lengths.
                "flex w-full items-center gap-3 rounded-2xl bg-pure-white/80 p-3 shadow-lg backdrop-blur-md transition-opacity duration-300 lg:gap-3.5 lg:px-4 lg:py-3",
                liveFeedVisible ? "opacity-100" : "opacity-0"
              )}
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gondola-blue-950 lg:h-[38px] lg:w-[38px]">
                <GondolaLogo
                  className="h-4 w-4 lg:h-5 lg:w-5"
                  color="hsl(var(--brand-gold))"
                />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex w-full items-center justify-between gap-2 lg:gap-3 lg:whitespace-nowrap">
                  <p className="min-w-0 font-figtree text-sm font-semibold leading-snug text-pure-black/90 lg:truncate lg:text-base">
                    {LIVE_FEED_ITEMS[liveFeedIndex].headline}
                  </p>
                  <span className="hidden shrink-0 font-figtree text-xs font-medium leading-snug text-pure-black/50 min-[361px]:block">
                    {LIVE_FEED_ITEMS[liveFeedIndex].timestamp}
                  </span>
                </div>
                <p className="text-pretty font-figtree text-sm font-normal leading-tight text-pure-black/75 lg:text-base lg:leading-normal">
                  {LIVE_FEED_ITEMS[liveFeedIndex].body}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <GoogleAuthModal
        headerText="Create an account"
        open={googleAuthModalOpen}
        onOpenChange={setGoogleAuthModalOpen}
      />
    </div>
  );
};

export default Hero;
